/**
 * Panel-specific client configuration — sets base URL and auth interceptors.
 * Call once before any API calls are made (in each panel's entry point).
 *
 * Unlike the web app, panels use body-based token refresh (not cookies)
 * because UXP fetch and CEP's Node.js context don't share browser cookies.
 */

import { client, refreshToken as refreshTokenSdk } from "@hops/sdk";
import { useAuthStore } from "../auth/auth-store";
import { isTokenExpired } from "../auth/token-utils";

let refreshPromise: Promise<string | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function getTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function doRefresh(): Promise<string | null> {
  const { refreshToken: storedRefreshToken } = useAuthStore.getState();
  if (!storedRefreshToken) return null;

  try {
    const { data } = await refreshTokenSdk({
      body: { refreshToken: storedRefreshToken },
      client,
    });

    if (data?.token) {
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore
          .getState()
          .setAuth(
            data.token,
            data.refreshToken ?? storedRefreshToken,
            currentUser,
          );
      }
      scheduleTokenRefresh(data.token);
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
}

function deduplicatedRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function scheduleTokenRefresh(token: string) {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) return;

  const now = Date.now();
  const timeUntilExpiry = expiryMs - now;
  if (timeUntilExpiry <= 0) return;

  const buffer = Math.min(timeUntilExpiry * 0.2, 30_000);
  const refreshIn = Math.max(timeUntilExpiry - buffer, 0);

  refreshTimer = setTimeout(() => {
    deduplicatedRefresh();
  }, refreshIn);
}

/**
 * Update the base URL of the already-configured client.
 * Call this when the user changes the server URL at runtime.
 * Does NOT re-register interceptors (they are added once by configureClient).
 */
export function updateBaseUrl(url: string) {
  client.setConfig({ baseUrl: url });
}

/**
 * Configure the shared hey-api client for panel use.
 * Call once at panel startup.
 */
export function configureClient(baseUrl: string) {
  client.setConfig({ baseUrl });

  // Request interceptor: inject JWT bearer token
  client.interceptors.request.use((request) => {
    const token = useAuthStore.getState().token;
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  });

  // Response interceptor: refresh on 401, then retry once
  client.interceptors.response.use(async (response, request, options) => {
    if (response.status !== 401) return response;

    // Don't attempt refresh for auth endpoints
    const url = new URL(request.url);
    if (url.pathname.startsWith("/api/auth/")) return response;

    const newToken = await deduplicatedRefresh();
    if (!newToken) {
      useAuthStore.getState().clearAuth();
      return response;
    }

    // Retry with new token
    const retryHeaders = new Headers(request.headers);
    retryHeaders.set("Authorization", `Bearer ${newToken}`);

    const retryInit: RequestInit = {
      method: request.method,
      headers: retryHeaders,
    };

    if (
      options &&
      typeof options === "object" &&
      "serializedBody" in options &&
      options.serializedBody != null
    ) {
      retryInit.body = options.serializedBody as BodyInit;
    }

    return fetch(request.url, retryInit);
  });

  // Auto-schedule refresh when token changes
  useAuthStore.subscribe((state, prev) => {
    if (state.token && state.token !== prev.token) {
      scheduleTokenRefresh(state.token);
    } else if (!state.token && refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  });

  // If we have a persisted token at startup, schedule or refresh
  const initialToken = useAuthStore.getState().token;
  if (initialToken) {
    if (isTokenExpired(initialToken)) {
      deduplicatedRefresh().then((newToken) => {
        if (!newToken) useAuthStore.getState().clearAuth();
      });
    } else {
      scheduleTokenRefresh(initialToken);
    }
  }
}
